import React from 'react';
import { Edit3, X } from 'lucide-react';
import { SmallText } from '../../../components/brand/BrandTypography';
import { IconButton, PrimaryButton, TertiaryButton } from '../../../components/brand/BrandButton';

const EmotionsTab = ({ emotions, editingEmotion, setEditingEmotion, onSave }) => {
    return (
        <div className="space-y-4">
            <div className="mb-4 p-4 bg-[#FFBB98]/20 rounded-xl border border-[#FFBB98]/20">
                <SmallText className="text-[#FFBB98]">
                    <strong>Emotions Reference Guide</strong> - Click on any emotion to edit it. These emotions appear in the Break the Cycle and ADHD Triggers tools.
                </SmallText>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {emotions.map(emotion => (
                    <div 
                        key={emotion.emotion_id} 
                        className="bg-white rounded-xl border border-[#FFBB98] p-4 cursor-pointer hover:border-[#FFBB98]/50 transition-colors"
                        onClick={() => setEditingEmotion({...emotion})}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                style={{ backgroundColor: emotion.color }}
                            >
                                {emotion.name.charAt(0)}
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-[#1D6B6B]">{emotion.name}</h4>
                                <span className="text-xs text-[#7D8E95]">{emotion.variants}</span>
                            </div>
                            <Edit3 className="w-4 h-4 text-[#7D8E95]" />
                        </div>
                        <div className="space-y-2 text-sm">
                            <p className="text-[#7D8E95] italic">"{emotion.inner_voice?.split('/')[0]?.trim()}"</p>
                            <div className="pt-2 border-t border-[#FFBB98]">
                                <span className="text-xs text-[#1D6B6B] font-medium">Need:</span>
                                <p className="text-xs text-[#7D8E95]">{emotion.underlying_need}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            {editingEmotion && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium">Edit Emotion</h3>
                            <IconButton onClick={() => setEditingEmotion(null)}>
                                <X className="w-5 h-5" />
                            </IconButton>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-[#7D8E95] mb-1 block">Name</label>
                                <input 
                                    value={editingEmotion.name} 
                                    onChange={(e) => setEditingEmotion({...editingEmotion, name: e.target.value})}
                                    className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-[#7D8E95] mb-1 block">Variants (comma separated)</label>
                                <input 
                                    value={editingEmotion.variants} 
                                    onChange={(e) => setEditingEmotion({...editingEmotion, variants: e.target.value})}
                                    className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-[#7D8E95] mb-1 block">Inner Voice</label>
                                <textarea 
                                    value={editingEmotion.inner_voice} 
                                    onChange={(e) => setEditingEmotion({...editingEmotion, inner_voice: e.target.value})}
                                    className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className="text-sm text-[#7D8E95] mb-1 block">Body Cues</label>
                                <textarea 
                                    value={editingEmotion.body_cues} 
                                    onChange={(e) => setEditingEmotion({...editingEmotion, body_cues: e.target.value})}
                                    className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                    rows={2}
                                />
                            </div>
                            <div>
                                <label className="text-sm text-[#7D8E95] mb-1 block">Underlying Need</label>
                                <input 
                                    value={editingEmotion.underlying_need} 
                                    onChange={(e) => setEditingEmotion({...editingEmotion, underlying_need: e.target.value})}
                                    className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-[#7D8E95] mb-1 block">Underlying Fear</label>
                                <input 
                                    value={editingEmotion.underlying_fear} 
                                    onChange={(e) => setEditingEmotion({...editingEmotion, underlying_fear: e.target.value})}
                                    className="w-full p-3 border border-[#FFBB98] rounded-xl"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-[#7D8E95] mb-1 block">Color</label>
                                <input 
                                    type="color"
                                    value={editingEmotion.color} 
                                    onChange={(e) => setEditingEmotion({...editingEmotion, color: e.target.value})}
                                    className="w-20 h-10 border border-[#FFBB98] rounded-xl cursor-pointer"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <TertiaryButton onClick={() => setEditingEmotion(null)} className="flex-1">Cancel</TertiaryButton>
                            <PrimaryButton onClick={() => onSave(editingEmotion)} className="flex-1">Save</PrimaryButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmotionsTab;
